import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { AlertSeverity, AlertUrgency } from '../types/alert';

export function formatDateSafe(isoString: string | null | undefined): string {
  if (!isoString) return 'Not specified';
  try {
    const d = parseISO(isoString);
    if (!isValid(d)) return 'Invalid date';
    return format(d, 'MMM d, yyyy h:mm a zzz');
  } catch {
    return 'Invalid date';
  }
}

export const formatFullDateTime = formatDateSafe;

export function formatRelativeTime(isoString: string | null | undefined): string {
  if (!isoString) return '';
  try {
    const d = parseISO(isoString);
    if (!isValid(d)) return '';
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return '';
  }
}

export function isAlertActive(expiresIso: string | null | undefined): boolean {
  if (!expiresIso) return true;
  try {
    const d = parseISO(expiresIso);
    return d.getTime() > Date.now();
  } catch {
    return true;
  }
}

export function getSeverityRank(severity: AlertSeverity): number {
  switch (severity) {
    case 'Extreme': return 4;
    case 'Severe': return 3;
    case 'Moderate': return 2;
    case 'Minor': return 1;
    default: return 0;
  }
}

export interface SeverityVisuals {
  label: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
  badgeClass: string;
  iconName: 'AlertOctagon' | 'AlertTriangle' | 'AlertCircle' | 'Info';
}

export function getSeverityVisuals(severity: AlertSeverity): SeverityVisuals {
  switch (severity) {
    case 'Extreme':
      return {
        label: 'Extreme Severity',
        bgClass: 'bg-red-50',
        textClass: 'text-red-900',
        borderClass: 'border-red-300',
        badgeClass: 'bg-red-600 text-white',
        iconName: 'AlertOctagon',
      };
    case 'Severe':
      return {
        label: 'Severe Disruption',
        bgClass: 'bg-orange-50',
        textClass: 'text-orange-900',
        borderClass: 'border-orange-300',
        badgeClass: 'bg-orange-600 text-white',
        iconName: 'AlertTriangle',
      };
    case 'Moderate':
      return {
        label: 'Moderate Advisory',
        bgClass: 'bg-amber-50',
        textClass: 'text-amber-900',
        borderClass: 'border-amber-300',
        badgeClass: 'bg-amber-600 text-white',
        iconName: 'AlertCircle',
      };
    case 'Minor':
      return {
        label: 'Minor Alert',
        bgClass: 'bg-blue-50',
        textClass: 'text-blue-900',
        borderClass: 'border-blue-300',
        badgeClass: 'bg-blue-600 text-white',
        iconName: 'Info',
      };
    default:
      return {
        label: 'Informational',
        bgClass: 'bg-slate-100',
        textClass: 'text-slate-800',
        borderClass: 'border-slate-300',
        badgeClass: 'bg-slate-600 text-white',
        iconName: 'Info',
      };
  }
}

